import { Component, ElementRef, HostListener, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { SecurityService } from '../../../core/security/security.service';
import { UserJsVm } from '../../../shared/generated/Administration/Models/User/JavaScript/UserJsVm';
import { GlobalService } from '../../service/global.service';
import { NavRoute } from '../nav.route.interface';
import { NavDividerCharacter, NavigationService } from '../navigation.service';

/**
 * This component is intended to be used in the top header bar
 * to create a nested navigation. Dropdowns are opened by
 * click.
 */
@Component({
    selector: 'pcg-top-click-nav',
    templateUrl: './top-click-nav.component.html',
    styleUrls: ['./top-click-nav.component.scss'],
    standalone: false
})
export class TopClickNavComponent implements OnInit, OnChanges {

	@Input() navRoutes: NavRoute[] = [];
	@Input() menuItemId = 'root';
	@Input() isRoot = true;

	user!: UserJsVm;
	isOpen!: boolean;
	currNavMenuItem!: string;
	currSelectedNavMenuItem!: string;
	currOpenSecondaryNavMenu!: string;
	currForceSelect!: string;
	ulClasses!: string[];
	loopNavRoutes: NavRoute[] = [];
	userNavOpen = false;
    isMobile!: boolean;

	constructor(
		public navService: NavigationService
		, public elRef: ElementRef
		, public sec: SecurityService
		, private router: Router
		, private modalService: NgbModal
	) {}

	@HostListener('window:resize')
	onWindowResize() { this.isMobile = GlobalService.setIsMobile(window.innerWidth); }

	ngOnInit() {
		this.isMobile = GlobalService.setIsMobile(window.innerWidth);

		// Get the current user information
		this.sec.user$.subscribe(user => { this.user = user; });
		// Triggred on update of currently OPEN menu item
		this.navService.currOpenPrimaryNavMenu$.subscribe(c => {
			this.currNavMenuItem = c;
			this.isOpen = this.isSubNav(c, this.menuItemId) || c === this.menuItemId;
			this.userNavOpen = c === ['root', 'user'].join(NavDividerCharacter);
			// The first ul should have classes indicating it is the root.
			// Nested ul elements, those at higher depths, are dropdown menus
			// and need classes indicating that.
			this.ulClasses = this.isRoot
				? ['nav', 'navbar-nav', 'top-nav-bg', this.isOpen ? 'nav-open' : '']
				: ['dropdown-menu', 'top-nav-bg', this.isOpen ? 'nav-open' : ''];
		});
		// Triggered on update of currently SELECTED menu item
		this.navService.currSelectedMainNavMenu$.subscribe(c => { this.currSelectedNavMenuItem = c ? c.id! : 'root'; });
		this.navService.currOpenSecondaryNavMenu$.subscribe(c => { this.currOpenSecondaryNavMenu = c ? c.id! : 'root'; });
		this.navService.currForceSelect$.subscribe(c => {
			this.currForceSelect = c;
			this.loopNavRoutes = this.getNavRoutes();
		});
	}

    isSubNav(
		currNavMenuItem: any
		, menuItemId: any
	) {
		// Get divider counts
		const divCountCurrNav = currNavMenuItem.split(NavDividerCharacter)?.length;
		const divCountMenuItem = menuItemId.split(NavDividerCharacter)?.length;

		// Sub navs and current nav should be open
		return divCountMenuItem < divCountCurrNav && currNavMenuItem.startsWith(this.menuItemId);
	}

	ngOnChanges(changes: SimpleChanges) {
		const chng = changes['navRoutes'];
		const cur = chng.currentValue;
		const prev = chng.previousValue;
		if (cur !== prev) { this.loopNavRoutes = this.getNavRoutes(); }
	}

	getHtmlId(routeId: string) { return routeId.replace(/^[^a-z]+|[^\w:.-]+/gi, ''); }

	openDropdown(childMenuId: string) {
		if (childMenuId === 'user') { childMenuId = ['root', 'user'].join(NavDividerCharacter); }
		this.navService.setCurrOpenNavMenu(childMenuId); 
		return false;
	}

    clickDropdown(
		childMenuId: string
		, routeHref: string = ""
	) {
		if (childMenuId === 'user') { childMenuId = ['root', 'user'].join(NavDividerCharacter); }
		if (this.currNavMenuItem.startsWith(childMenuId)) { this.navService.setCurrOpenNavMenu(this.menuItemId); } 
		else { this.navService.setCurrOpenNavMenu(childMenuId); } // Otherwise, open the submenu		
		if (
			routeHref !== '' 
			&& window.innerWidth > 992
		) { window.location.href = routeHref; }
		return false;
	}

    closeDropdown(childMenuId: string) {
		if (!this.isMobile) { 
			if (childMenuId === 'user') { childMenuId = ['root', 'user'].join(NavDividerCharacter); }
			this.navService.setCurrOpenNavMenu(this.menuItemId); 		
		}		
		return false;
	}

    clickLink(route: NavRoute) { 
		this.navService.setCurrOpenNavMenu(''); 
		if (route != null) {
			if (route.openNewTab) { window.open(route.path); }
			else { this.router.navigate([route.path], {queryParams: route.queryParams}); }
		}
	}

	getNavRoutes() {
		const routeIndex = this.navService.getCurrRouteIndex(this.navRoutes);
		return (
			this.navRoutes
				?.map((o, i) => ({ ...o, index: i }))
				.filter(route => 
					(route?.children == null || route?.children?.length > 0) 
					&& (!route?.shouldDisplay || route?.shouldDisplay(routeIndex!))
				) ?? []
		);
	}
}
